import React, { useState } from 'react';
import {
  TypographyVariant,
  WithTheme,
  bem,
  cx,
  interpolateFontSize,
} from '@mint/core';
import { useFocus, useFormControlClassNames, useValue } from '../shared';
import { FormControl } from '../form-control';
import { InputHelperMessage } from '../input-helper-message';
import { InputLabel } from '../input-label';
import { InputUnderline } from '../input-underline';
import { TextareaBase } from '../textarea-base';
import { TextareaProps } from './textarea-props';
import styled from 'styled-components';
import { useTextareaSizer } from './use-textarea-sizer';

/**
 * Base input root class name applied to the input element.
 */
const ROOT_CLASS_NAME = 'textarea';

/**
 * BEM block name for the composed label component.
 */
const LABEL_BLOCK_NAME = 'label';

/**
 * BEM block name for the composed base input component.
 */
const INPUT_BLOCK_NAME = 'textarea';

/**
 * BEM block name for the underline component.
 */
const UNDERLINE_BLOCK_NAME = 'underline';

/**
 * Helper function to creat the helper text ID.
 * @param id Input `id` property.
 */
const makeHelperTextId = (id?: string): string | undefined =>
  id ? `${id}__helper-text` : undefined;

/**
 * `Input` component implementation before styles are applied.
 */
const TextareaComponent = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref): React.ReactElement => {
    const {
      children,
      className,
      component,
      dense,
      disabled,
      error,
      helperText,
      id,
      label,
      onBlur,
      onChange,
      onFocus,
      value: valueProp,
      warning,
      ...rest
    } = props;

    const [focused, bindFocus] = useFocus({ onBlur, onFocus });
    const [value, handleChange] = useValue({
      onChange,
      value: valueProp as string,
    });
    const [height, setHeight] = useState<number | undefined>();

    const hasValue = value.length > 0;
    const classes = bem(ROOT_CLASS_NAME);

    const sizer = useTextareaSizer(value, (h): void => setHeight(h));

    return (
      <FormControl
        className={cx(
          ROOT_CLASS_NAME,
          useFormControlClassNames(ROOT_CLASS_NAME, { ...props, focused }),
          props.dense && classes.dense(),
          hasValue ? classes.hasValue() : classes.empty(),
          props.className,
        )}
        component={component}
        dense={dense}
      >
        {label != null && (
          <InputLabel
            className={cx(bem.block(ROOT_CLASS_NAME, LABEL_BLOCK_NAME))}
            dense={dense}
            error={error}
            focused={focused}
            htmlFor={id}
            shrink={focused || hasValue}
            warning={warning}
          >
            {label}
          </InputLabel>
        )}
        <TextareaBase
          aria-describedby={makeHelperTextId(id)}
          className={cx(bem.block(ROOT_CLASS_NAME, INPUT_BLOCK_NAME))}
          disabled={disabled}
          dense={dense}
          hidePlaceholder={props.label != null && !focused}
          {...rest}
          {...bindFocus}
          id={id}
          ref={ref}
          onChange={handleChange}
          style={{ height }}
          value={value}
        />
        {sizer}
        <InputUnderline
          className={cx(bem.block(ROOT_CLASS_NAME, UNDERLINE_BLOCK_NAME))}
          disabled={disabled}
          error={error}
          focused={focused}
          warning={warning}
        />
        {helperText != null && (
          <InputHelperMessage
            error={error}
            focused={focused}
            id={makeHelperTextId(id)}
            warning={warning}
          >
            {helperText}
          </InputHelperMessage>
        )}
      </FormControl>
    );
  },
);

TextareaComponent.displayName = 'Textarea';

const pickTypographyVariant = (
  props: WithTheme<TextareaProps>,
): TypographyVariant => (props.dense ? 'dense' : 'control');

const paddingTop = (props: WithTheme<TextareaProps>): string =>
  props.label ? '1em' : '0';

const minHeight = (props: WithTheme<TextareaProps>): string => {
  if (props.dense) {
    return `calc(${props.theme.typography.dense.lineHeight} * 3em)`;
  } else {
    return `calc(${props.theme.typography.control.lineHeight} * 3em)`;
  }
};

export const Textarea = styled(TextareaComponent)`
  padding-top: ${paddingTop};
  position: relative;
  ${interpolateFontSize(pickTypographyVariant)}

  ${InputHelperMessage} {
    margin-top: 0.2em;
  }

  ${InputLabel} {
    position: absolute;
  }

  ${TextareaBase} {
    min-height: ${minHeight};
    resize: none;
  }

  ${TextareaBase}::placeholder {
    transition: opacity 200ms;
  }
`;
